import { Box, Skeleton, Typography, IconButton, MenuList, MenuItem } from '@mui/material';
import CommonPopover from '@shared/components/ui/CommonPopover';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import UtmsButton from '@shared/components/ui/UtmsButton';
import UtmsChip from '@shared/components/ui/UtmsChip';
import { FC, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFECT_STATUS_COLOR } from '../constants';
import { Defect, DefectStatusEnum } from '../types';
import LoadingPlaceholder from '@shared/components/ui/LoadingPlaceHolder';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CachedIcon from '@mui/icons-material/Cached';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { selectGetDefectStatus } from '../store/selectors';
import { RequestStatusEnum } from '@shared/types';

interface DefectCardHeaderProps {
    defect: Defect | null;
    onEdit: () => void;
    onClose: () => void;
    onReopen: () => void;
    onDelete: () => void;
}

const DefectCardHeader: FC<DefectCardHeaderProps> = ({ defect, onEdit, onClose, onReopen, onDelete }) => {
    const { t } = useTranslation();
    const [actionsPopoverAnchorEl, setActionsPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
    const { pk, name, status } = defect ?? {};
    const isDefectOpen = defect?.status === DefectStatusEnum.OPEN;
    const getDefectStatus = useSelector(selectGetDefectStatus);
    const isDefectLoading = getDefectStatus === RequestStatusEnum.Loading;

    const handleOpenActionsPopover = (event: MouseEvent<HTMLButtonElement>) => {
        setActionsPopoverAnchorEl(event.currentTarget);
    };

    const handleToggleStatus = () => {
        isDefectOpen ? onClose() : onReopen();
        setActionsPopoverAnchorEl(null);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                <LoadingPlaceholder
                    isLoading={isDefectLoading}
                    skeleton={
                        <>
                            <Skeleton width={'60%'} animation="wave" sx={{ fontSize: 20 }} />
                            <Skeleton variant="rounded" width={62} height={18} animation="wave" />
                        </>
                    }
                >
                    {defect && (
                        <>
                            <OverflowTooltip title={name}>
                                <Typography noWrap sx={{ fontSize: 20, fontWeight: 700 }}>
                                    #{pk} {name}
                                </Typography>
                            </OverflowTooltip>
                            {status && <UtmsChip label={t(status.toLowerCase())} color={DEFECT_STATUS_COLOR[status]} />}
                        </>
                    )}
                </LoadingPlaceholder>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexShrink: 0 }}>
                <LoadingPlaceholder
                    isLoading={isDefectLoading}
                    skeleton={
                        <>
                            <Skeleton animation="wave" width={113} variant="rounded" height={36} />
                            <Skeleton animation="wave" variant="circular" height={36} width={36} />
                        </>
                    }
                >
                    {defect && (
                        <>
                            <UtmsButton startIcon={<EditIcon />} onClick={onEdit}>
                                {t('edit')}
                            </UtmsButton>
                            <IconButton color="primary" onClick={handleOpenActionsPopover}>
                                <MoreVertIcon />
                            </IconButton>
                            <CommonPopover anchorEl={actionsPopoverAnchorEl} setAnchorEl={setActionsPopoverAnchorEl}>
                                <MenuList>
                                    <MenuItem
                                        onClick={handleToggleStatus}
                                        sx={{ display: 'flex', gap: 1.25, fontSize: 16 }}
                                    >
                                        {isDefectOpen ? (
                                            <>
                                                <CloseIcon color="action" /> {t('close')} {t('defect').toLowerCase()}
                                            </>
                                        ) : (
                                            <>
                                                <CachedIcon color="action" />
                                                {t('reopen_defect')}
                                            </>
                                        )}
                                    </MenuItem>
                                    <MenuItem onClick={onDelete} sx={{ display: 'flex', gap: 1.25, fontSize: 16 }}>
                                        <DeleteIcon color="action" /> {t('delete')} {t('defect').toLowerCase()}
                                    </MenuItem>
                                </MenuList>
                            </CommonPopover>
                        </>
                    )}
                </LoadingPlaceholder>
            </Box>
        </Box>
    );
};

export default DefectCardHeader;
